/* eslint-disable max-len */
import { useEffect } from 'react';
import { Table, Input } from 'reactstrap';
import { Controller, useFieldArray } from 'react-hook-form';
import Icon from '@mdi/react';
import { mdiTrashCanOutline } from '@mdi/js';
import { fetchTravelCountry } from '../../../store/apis/autocompleteService';
import BookingAsyncAutocomplete from '../../BookingAgent/FlightOption/BookingAsyncAutocomplete';
import { DatePicker } from '../../Common';
import './HotelInformation.scss';

function HotelInformation({
  isEdit,
  isView = false,
  control,
  errors,
  detailsData,
}) {
  const hotelDetails = detailsData?.hotelDetails;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'hotelInformation',
  });

  const removeRow = index => {
    if (fields.length > 1) {
      remove(index);
    } else {
      replace({});
    }
  };

  useEffect(() => {
    if (
      isEdit &&
      hotelDetails &&
      Array.isArray(hotelDetails) &&
      hotelDetails.length > 0
    ) {
      // Map API fields to form fields
      const mapped = hotelDetails.map(item => ({
        place: item?.Place?.trim(),
        checkInDate: item?.CheckInDate?.trim()?.split('T')?.[0],
        checkOutDate: item?.CheckOutDate?.trim()?.split('T')?.[0],
        preferredHotel: item?.HotelName?.trim(),
        traveller: item?.ClientHotel?.trim(),
        country: {
          value: item?.Country?.trim(),
          label: item?.Country_Description?.trim(),
        },
      }));
      replace(mapped);
    } else if (fields.length === 0) {
      append({});
    }
  }, [hotelDetails, replace, append]);

  return (
    <div className="new-travel-request-card p-3 bg-white">
      <div className="d-flex flex-row align-items-center justify-content-between w-100 mb-2">
        <div className="fw-semibold fs-5">Hotel Information</div>
        <button
          type="button"
          className="custom-secondary-btn"
          onClick={() => append({})}
          disabled={isView}
        >
          Add New
        </button>
      </div>
      <div className="table-responsive">
        <div className="ntr-table-container">
          <Table
            className="options-table align-middle text-nowrap ntr-table"
            id="hotel-tr-table"
          >
            <thead>
              <tr>
                <th className="text-center px-2">#</th>
                <th className="px-2">
                  Country <span className="text-danger-tr">*</span>
                </th>
                <th className="px-2">
                  Place <span className="text-danger-tr">*</span>
                </th>
                <th className="px-2">
                  Check In Date <span className="text-danger-tr">*</span>
                </th>
                <th className="px-2">
                  Check Out Date <span className="text-danger-tr">*</span>
                </th>
                <th className="px-2">Pref. Hotel</th>
                <th className="px-2">Traveller</th>
                <th className="px-2 text-center"> </th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td className="text-center px-2 py-1">{index + 1}.</td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`hotelInformation.${index}.country`}
                      render={({ field: infield }) => (
                        <BookingAsyncAutocomplete
                          {...infield}
                          autoCompleteFn={input =>
                            fetchTravelCountry({
                              prefixText: input,
                            })
                          }
                          mappedFn={rawData =>
                            rawData.map(item => ({
                              value: item.Code,
                              label: item.Description,
                            }))
                          }
                          value={infield.value} // {formatSelectValue(infield.value, 20)}
                          onChange={infield.onChange}
                          placeholder="Country"
                          isClearable
                          displayOption="bothCustom"
                          inputWidth="100%"
                          width="100%"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 2000 }),
                          }}
                          isDisabled={isView}
                        />
                      )}
                    />
                    {errors.hotelInformation?.[index]?.country?.value && (
                      <div
                        className="text-danger-tr text-wrap"
                        style={{ marginLeft: '11px' }}
                      >
                        {errors.hotelInformation[index].country.value.message}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    <Controller
                      control={control}
                      name={`hotelInformation.${index}.place`}
                      render={({ field: element }) => (
                        <Input
                          {...element}
                          placeholder="Place"
                          className="border-0 ps-0"
                          disabled={isView}
                        />
                      )}
                    />
                    {errors.hotelInformation?.[index]?.place && (
                      <div className="text-danger-tr text-wrap">
                        {errors.hotelInformation[index].place.message}
                      </div>
                    )}
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`hotelInformation.${index}.checkInDate`}
                      render={({ field: element }) => (
                        <DatePicker
                          {...element}
                          className="border-0 pe-2"
                          disabled={isView}
                          calendarFocus={false}
                        />
                      )}
                    />
                    {errors.hotelInformation?.[index]?.checkInDate && (
                      <div
                        className="text-danger-tr text-wrap"
                        style={{ marginLeft: '11px' }}
                      >
                        {errors.hotelInformation[index].checkInDate.message}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    <Controller
                      control={control}
                      name={`hotelInformation.${index}.checkOutDate`}
                      render={({ field: element }) => (
                        <DatePicker
                          {...element}
                          className="border-0 pe-2"
                          disabled={isView}
                          calendarFocus={false}
                        />
                      )}
                    />
                    {errors.hotelInformation?.[index]?.checkOutDate && (
                      <div
                        className="text-danger-tr text-wrap"
                        style={{ marginLeft: '11px' }}
                      >
                        {errors.hotelInformation[index].checkOutDate.message}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1">
                    <Controller
                      control={control}
                      name={`hotelInformation.${index}.preferredHotel`}
                      render={({ field: element }) => (
                        <Input
                          {...element}
                          placeholder="Pref. Hotel"
                          className="border-0 ps-0"
                          disabled={isView}
                        />
                      )}
                    />
                  </td>
                  <td className="p-0 py-1">
                    <Controller
                      control={control}
                      name={`hotelInformation.${index}.traveller`}
                      render={({ field: element }) => (
                        <Input
                          {...element}
                          placeholder="Traveller"
                          className="border-0"
                          disabled={isView}
                        />
                      )}
                    />
                  </td>
                  <td className="text-center px-3">
                    {index >= 0 && (
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => removeRow(index)}
                        disabled={isView}
                      >
                        <Icon
                          path={mdiTrashCanOutline}
                          size={0.7}
                          color="#D92D20"
                        />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default HotelInformation;
